import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/request-password-reset", { email: email.trim() });
            setSent(true);
            toast.success("If that email is registered, a reset link is on the way.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ok-container py-16 max-w-md mx-auto" data-testid="forgot-password-page">
            <div className="text-center mb-8">
                <div className="inline-block"><Logo /></div>
            </div>
            <div className="ok-card p-8">
                <h1 className="font-display text-2xl mb-1">Reset your password</h1>
                <p className="text-sm opacity-60 mb-6">Enter your account email. We&apos;ll send a secure link.</p>

                {sent ? (
                    <div className="space-y-4" data-testid="forgot-password-sent">
                        <div className="ok-card p-4 border-[var(--ok-accent)] text-sm">
                            <div className="flex items-start gap-3">
                                <Mail className="w-4 h-4 mt-0.5 text-[var(--ok-accent)] flex-shrink-0" />
                                <div>
                                    <div className="font-medium">Check your inbox</div>
                                    <div className="opacity-70 text-xs mt-1 leading-relaxed">
                                        If <span className="font-mono">{email}</span> is on file, you&apos;ll receive a reset link within a few minutes. The link is valid for 60 minutes.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Link to="/login" className="ok-btn-outline w-full justify-center" data-testid="forgot-password-back-to-login">
                            Back to sign in
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                        <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest opacity-60 block mb-1.5">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="ok-input"
                                data-testid="forgot-password-email"
                                autoFocus
                            />
                        </div>
                        <button type="submit" disabled={loading} className="ok-btn-primary w-full justify-center" data-testid="forgot-password-submit">
                            {loading ? "Sending…" : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
                        </button>
                        <p className="text-xs text-center opacity-60 pt-2">
                            <Link to="/login" className="ok-link" data-testid="forgot-password-cancel">Back to sign in</Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}
